import { CheckCircle2, CloudLightning, CloudRain, Flag, Droplets } from 'lucide-react';
import { detectMorningFog } from '../lib/morning';
import { courseRainWarning, findPlayableWindows, hourHasLightning } from '../lib/playable';
import type { HourlyEntry } from '../types';

export default function PlayableWindow({
  hourly,
  timeZone,
  rainPast48hIn,
}: {
  hourly: HourlyEntry[];
  timeZone: string;
  rainPast48hIn: number;
}) {
  const horizon = Math.min(24, hourly.length);
  const windows = findPlayableWindows(hourly, horizon);
  const lightningHours = hourly.slice(0, horizon).filter((h) => hourHasLightning(h.condition));
  const allClear = lightningHours.length === 0;
  const rainWarn = courseRainWarning(rainPast48hIn);
  const morning = detectMorningFog(hourly, timeZone);

  const fmt = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    });

  return (
    <section>
      <h2 className="text-xs uppercase tracking-wider opacity-70 mb-2 flex items-center gap-2">
        <Flag className="w-3.5 h-3.5" /> Playable (next 24h)
      </h2>

      {rainWarn && (
        <div className="mb-2 flex items-start gap-3 bg-amber-500/25 border border-amber-400/50 rounded-lg p-3">
          <CloudRain className="w-5 h-5 shrink-0 mt-0.5 text-amber-200" />
          <div className="text-sm">{rainWarn}</div>
        </div>
      )}

      {morning.active && morning.message && (
        <div className="mb-2 flex items-start gap-3 bg-sky-500/20 border border-sky-400/40 rounded-lg p-3">
          <Droplets className="w-5 h-5 shrink-0 mt-0.5 text-sky-200" />
          <div className="text-sm">{morning.message}</div>
        </div>
      )}

      {allClear ? (
        <div className="flex items-center gap-3 bg-emerald-500/20 border border-emerald-400/40 rounded-lg p-3">
          <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-300" />
          <div className="text-sm">
            <div className="font-medium">All 24 hours playable</div>
            <div className="text-xs opacity-80">No thunderstorms in the forecast.</div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs opacity-80">
            <CloudLightning className="w-3.5 h-3.5 text-amber-300" />
            Thunderstorms forecast — non-lightning windows:
          </div>
          {windows.map((w, i) => (
            <div
              key={w.start}
              className={`bg-white/10 backdrop-blur-sm rounded-lg p-3 ${
                i === 0 ? 'border border-emerald-400/40' : ''
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">
                    {fmt(w.start)} – {fmt(w.end)}
                  </div>
                  <div className="text-xs opacity-70">
                    {w.hours} hour{w.hours === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="text-xs opacity-90 tabular-nums">
                  {w.avgTempF}° · wind {w.maxWindMph} · precip {w.maxPrecipPct}%
                </div>
              </div>
            </div>
          ))}
          {windows.length === 0 && (
            <div className="bg-rose-500/20 border border-rose-400/40 rounded-lg p-3 text-sm">
              Thunderstorms every hour in the next 24h. Don't be on the course.
            </div>
          )}
        </div>
      )}

      <div className="text-[10px] opacity-50 mt-1">
        Filter: only thunderstorms excluded. Everything else is playable.
      </div>
    </section>
  );
}
